import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetNotificationUnreadCount,
  useListNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  getGetNotificationUnreadCountQueryKey,
  getListNotificationsQueryKey,
} from "@workspace/api-client-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, BellOff, CheckCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "wouter";

const TYPE_LABELS: Record<string, string> = {
  approval: "Approval",
  mention: "Mention",
  renewal: "Renewal",
  expiring_contract: "Expiring Contract",
  expiring_insurance: "Expiring Insurance",
  billing: "Billing",
  system: "System",
};

const TYPE_COLORS: Record<string, string> = {
  approval: "bg-blue-500/10 text-blue-700 border-blue-200",
  mention: "bg-violet-500/10 text-violet-700 border-violet-200",
  renewal: "bg-amber-500/10 text-amber-700 border-amber-200",
  expiring_contract: "bg-orange-500/10 text-orange-700 border-orange-200",
  expiring_insurance: "bg-red-500/10 text-red-700 border-red-200",
  billing: "bg-rose-500/10 text-rose-700 border-rose-200",
  system: "bg-gray-500/10 text-gray-700 border-gray-200",
};

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const countKey = getGetNotificationUnreadCountQueryKey();
  const listKey = getListNotificationsQueryKey({ limit: 10 });

  const { data: countData } = useGetNotificationUnreadCount({
    query: {
      queryKey: countKey,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    },
  });

  const { data: listData } = useListNotifications(
    { limit: 10 },
    {
      query: {
        queryKey: listKey,
        enabled: open,
      },
    },
  );

  const markAllRead = useMarkAllNotificationsRead();
  const markOneRead = useMarkNotificationRead();

  const unreadCount = countData?.count ?? 0;
  const notifications = listData?.items ?? [];

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: countKey });
    queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate(undefined, { onSuccess: invalidateAll });
  };

  const handleClick = async (n: {
    id: number;
    link: string | null;
    readAt: string | null;
  }) => {
    const isExternal = !!n.link && (
      n.link.startsWith("http://") || n.link.startsWith("https://") || n.link.startsWith("/api/")
    );
    if (!n.readAt) {
      if (isExternal) {
        // Await mark-read so it completes before page unload
        await markOneRead.mutateAsync({ id: n.id }).catch(() => undefined);
        invalidateAll();
      } else {
        markOneRead.mutate({ id: n.id }, { onSuccess: invalidateAll });
      }
    }
    setOpen(false);
    if (n.link) {
      if (isExternal) {
        window.location.assign(n.link);
      } else {
        setLocation(n.link);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold leading-none text-white bg-red-500 rounded-full px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
              <Link href="/notifications">View all</Link>
            </Button>
          </div>
        </div>
        <ScrollArea className="max-h-[420px]">
          {notifications.length === 0 ? (
            <div className="p-6 flex flex-col items-center gap-2 text-center text-muted-foreground">
              <BellOff className="h-8 w-8 opacity-30" />
              <p className="text-sm">No notifications yet.</p>
              <p className="text-xs">You'll see approvals, mentions, renewals, and more here.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${!n.readAt ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.readAt && (
                        <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                      )}
                      <div className={`flex-1 min-w-0 ${!n.readAt ? "" : "pl-4"}`}>
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <Badge
                            variant="outline"
                            className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 ${TYPE_COLORS[n.type] ?? TYPE_COLORS.system}`}
                          >
                            {TYPE_LABELS[n.type] ?? n.type}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground leading-snug">{n.title}</p>
                        {n.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
